import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Icon,
  useMedia,
  fontStyles as fs,
  LoadingIndicator,
  Button,
  IconButton,
} from '@monash/portal-react';
import c from './categories.module.scss';
import CategoryForm from './category-form/CategoryForm';
import { SlideOutContext } from '../../common/slide-out/SlideOutWrapper';
import { APIContext } from '@monash/portal-frontend-common';
import { CATEGORY_TYPES } from './constants';
import CategoryReorder from './category-reorder/CategoryReorder';

const Categories = () => {
  const { setSlide, setOpen, open } = useContext(SlideOutContext);
  const { getCategoriesOrder, getAllCategories, getAllLinks } =
    useContext(APIContext);

  const size = useMedia();

  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [catOrder, setCatOrder] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allLinks, setAllLinks] = useState([]);
  const [isReorderOpen, setIsReorderOpen] = useState(false);

  const getAllData = () => {
    getCategoriesOrder().then((order) => {
      setCatOrder(order);
      setLoadingOrder(false);
    });
    getAllCategories().then((cats) => {
      setCategories(cats);
      setLoadingCategories(false);
    });
    getAllLinks().then((links) => {
      setAllLinks(links);
    });
  };

  useEffect(() => {
    getAllData();
  }, []);

  const getRelevantLinks = useCallback(
    (catId) => {
      return allLinks.filter((link) =>
        link.categories.some((cat) => cat.id === catId)
      );
    },
    [allLinks]
  );

  const getSortOrder = (sectionOrder, id) => {
    const index = sectionOrder.indexOf(id);
    return index === -1 ? sectionOrder.length : index;
  };

  const sections = useMemo(() => {
    return CATEGORY_TYPES.map((cat) => {
      const section = { type: cat.value, items: [] };
      categories.forEach((item) => {
        if (item.type === cat.value) {
          section.items.push(item);
        }
      });
      return section;
    })
      .filter((section) => section.items.length > 0)
      .map((section) => {
        // sort based on catOrder
        // leave anything not in catOrder at the end
        const sectionOrder =
          catOrder.filter((o) => o.type === section.type)[0]?.order || [];

        const sortedItems = section.items.sort((a, b) => {
          const aIndex = getSortOrder(sectionOrder, a.id);
          const bIndex = getSortOrder(sectionOrder, b.id);
          return aIndex - bIndex;
        });
        return {
          ...section,
          items: sortedItems,
        };
      });
  }, [categories, catOrder]);

  const fullWidthColumns = size === 'L' ? 4 : 2;

  const openSlideForm = (selectedCategory, links) => {
    setSlide({
      content: (
        <CategoryForm
          data={selectedCategory}
          refreshData={getAllData}
          {...{ links, categories }}
          key={selectedCategory?.id || Math.random()}
        />
      ),
    });
    setOpen(true);
  };

  const actions =
    !open && size === 'L' ? (
      <div className={c.actions}>
        <Button
          icon={<Icon.Unit />}
          variant="text"
          onClick={() => setIsReorderOpen(true)}
        >
          Reorder categories
        </Button>
        <Button
          icon={<Icon.Add />}
          variant="primary"
          onClick={() => openSlideForm(null)}
        >
          Add Category
        </Button>
      </div>
    ) : (
      <div className={c.actions}>
        <IconButton
          icon={Icon.Unit}
          variant="text"
          onClick={() => setIsReorderOpen(true)}
        />
        <IconButton
          icon={Icon.Add}
          variant="primary"
          onClick={() => openSlideForm(null)}
        />
      </div>
    );

  return (
    <div className={c.container}>
      <div className={c.content}>
        <div>
          <div className={c.headerRow}>
            <h1>Categories</h1>
            {actions}
          </div>
          <span>
            This is where you manage the categories and subcategories that are
            displayed in the Links app. You will be able to create, amend and
            delete categories and subcategories, add new subcategories to
            existing categories, assign illustrations and change the order that
            categories are displayed in the Links app.
          </span>
        </div>
        {(loadingCategories || loadingOrder) && (
          <div className={c.loading}>
            <LoadingIndicator />
          </div>
        )}
        {sections.map((section) => (
          <div className={c.sectionContainer} key={section.type}>
            <h2>Categories by {section.type}</h2>
            <ul
              className={c.categoriesList}
              style={{
                gridTemplateColumns: `repeat(${
                  open ? fullWidthColumns / 2 : fullWidthColumns
                }, 1fr)`,
              }}
            >
              {section.items.map((item) => {
                const ItemIcon = Icon[item.icon] || Icon.QuestionMark;
                const links = getRelevantLinks(item.id);
                const openSlidey = () => openSlideForm(item, links);
                return (
                  <li
                    onClick={openSlidey}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        openSlidey();
                      }
                    }}
                    tabIndex={0}
                    key={item.id}
                    className={c.categoryCard}
                  >
                    <div className={c.category}>
                      <div className={c.icon}>
                        <ItemIcon />
                      </div>
                      <p className={[fs.sm, fs.medium].join(' ')}>
                        {item.title}
                      </p>
                      <p className={[c.linkCount, fs.label].join(' ')}>
                        {links.length} links
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <CategoryReorder
        isOpen={isReorderOpen}
        setIsOpen={setIsReorderOpen}
        sections={sections}
        setCatOrder={setCatOrder}
      />
    </div>
  );
};

export default Categories;
